import { useLocation, useSearchParams } from "react-router-dom";
import { useState } from "react";
import { CheckCircle2, Loader2, AlertCircle } from "lucide-react";
import { submitInquiry, submitPartnerInquiry } from "@/lib/api";

const SERVICE_OPTIONS = [
  ["", "Select a service (optional)"],
  ["marketing-website-design", "Marketing & Website Design"],
  ["veluro-ai-marketing", "Veluro — AI Marketing Platform"],
  ["employee-health-wellness", "Employee Health & Wellness"],
  ["medical-claims-billing", "Medical Claims & Billing"],
  ["shipping-freight", "Shipping & Freight"],
  ["emerging-opportunities", "Emerging Opportunities"],
  ["become-a-partner", "Become a Partner (Aspire)"],
  ["not-sure", "Not sure yet — help me explore"],
];

const BACKGROUNDS = [
  ["", "Select your background"],
  ["consultant-advisor", "Consultant or advisor"],
  ["executive-operator", "Executive or operator"],
  ["insurance-benefits-financial", "Insurance, benefits, or financial professional"],
  ["other", "Other"],
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export default function InquiryForm({ variant = "business", defaultService = "", title }) {
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  const interest = params.get("interest") || params.get("service") || defaultService;

  const initial = variant === "business"
    ? { name: "", email: "", company: "", phone: "", service: interest || "", message: "" }
    : { name: "", email: "", phone: "", company: "", background: "", message: "" };

  const [values, setValues] = useState(initial);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [serverError, setServerError] = useState("");

  const isPartner = variant === "partner";
  const set = (k) => (e) => setValues((v) => ({ ...v, [k]: e.target.value }));

  const validate = () => {
    const errs = {};
    if (values.name.trim().length < 2) errs.name = "Please enter your name.";
    if (!EMAIL_RE.test(values.email)) errs.email = "Please enter a valid email address.";
    if (!isPartner && values.company.trim().length < 2) errs.company = "Please enter your company name.";
    if (values.message.trim().length < 10) errs.message = "Please tell us a little more (at least 10 characters).";
    return errs;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setServerError("");
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setStatus("submitting");
    try {
      const payload = { ...values, source_page: pathname, website: "" };
      const fn = isPartner ? submitPartnerInquiry : submitInquiry;
      const res = await fn(payload);
      if (res.data?.status === "ok") setStatus("success");
      else throw new Error("Unexpected response");
    } catch (err) {
      setStatus("error");
      setServerError(
        err?.response?.data?.detail?.message ||
          err?.response?.data?.detail ||
          "Something went wrong while sending. Your entries are preserved — please try again."
      );
    }
  };

  const Error = ({ k }) =>
    errors[k] ? (
      <p className="mt-1.5 flex items-center gap-1 text-sm text-[#A33B2E]" role="alert" data-testid={`form-error-${k}`}>
        <AlertCircle className="h-3.5 w-3.5" /> {errors[k]}
      </p>
    ) : null;

  if (status === "success") {
    return (
      <div className="border border-forest/25 bg-sage/60 p-8 md:p-10" data-testid="form-success-panel">
        <CheckCircle2 className="h-8 w-8 text-forest" />
        <h3 className="mt-4 font-serif text-2xl text-forest">Thank you — your {isPartner ? "partner interest" : "inquiry"} was received.</h3>
        <p className="mt-3 text-charcoal/75">
          We’ll review your note and reach out to arrange a conversation. No pressure,
          no obligation — just a clear next step.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} noValidate data-testid={isPartner ? "partner-inquiry-form" : "business-inquiry-form"} className="space-y-5">
      {title && <h3 className="font-serif text-2xl text-charcoal">{title}</h3>}

      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor={`f-name-${variant}`} className="field-label">Name *</label>
          <input id={`f-name-${variant}`} className="field-input" value={values.name} onChange={set("name")} autoComplete="name" aria-invalid={!!errors.name} data-testid="form-input-name" />
          <Error k="name" />
        </div>
        <div>
          <label htmlFor={`f-email-${variant}`} className="field-label">{isPartner ? "Email *" : "Work email *"}</label>
          <input id={`f-email-${variant}`} type="email" className="field-input" value={values.email} onChange={set("email")} autoComplete="email" aria-invalid={!!errors.email} data-testid="form-input-email" />
          <Error k="email" />
        </div>
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        {isPartner ? (
          <div>
            <label htmlFor={`f-company-${variant}`} className="field-label">Organization (optional)</label>
            <input id={`f-company-${variant}`} className="field-input" value={values.company} onChange={set("company")} autoComplete="organization" data-testid="form-input-company" />
          </div>
        ) : (
          <div>
            <label htmlFor={`f-company-${variant}`} className="field-label">Company *</label>
            <input id={`f-company-${variant}`} className="field-input" value={values.company} onChange={set("company")} autoComplete="organization" aria-invalid={!!errors.company} data-testid="form-input-company" />
            <Error k="company" />
          </div>
        )}
        <div>
          <label htmlFor={`f-phone-${variant}`} className="field-label">Phone (optional)</label>
          <input id={`f-phone-${variant}`} type="tel" className="field-input" value={values.phone} onChange={set("phone")} autoComplete="tel" data-testid="form-input-phone" />
        </div>
      </div>

      {isPartner ? (
        <div>
          <label htmlFor={`f-bg-${variant}`} className="field-label">Which best describes your background?</label>
          <select id={`f-bg-${variant}`} className="field-input" value={values.background} onChange={set("background")} data-testid="form-select-background">
            {BACKGROUNDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
      ) : (
        <div>
          <label htmlFor={`f-service-${variant}`} className="field-label">Service of interest</label>
          <select id={`f-service-${variant}`} className="field-input" value={values.service} onChange={set("service")} data-testid="form-select-service">
            {SERVICE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
      )}

      <div>
        <label htmlFor={`f-message-${variant}`} className="field-label">Message *</label>
        <textarea id={`f-message-${variant}`} rows={5} className="field-input resize-y" value={values.message} onChange={set("message")} placeholder={isPartner ? "Tell us about your network and what you'd like to explore." : "Tell us what you're working toward."} aria-invalid={!!errors.message} data-testid="form-input-message" />
        <Error k="message" />
      </div>

      {/* Honeypot — hidden from humans, catches bots */}
      <div className="hidden" aria-hidden="true">
        <label htmlFor={`f-web-${variant}`}>Website</label>
        <input id={`f-web-${variant}`} name="website" tabIndex={-1} autoComplete="off" value={values.website || ""} onChange={set("website")} />
      </div>

      {status === "error" && (
        <p className="border border-[#A33B2E]/30 bg-[#A33B2E]/5 px-4 py-3 text-sm text-[#A33B2E]" role="alert" data-testid="form-server-error">
          {serverError}
        </p>
      )}

      <button
        type="submit"
        disabled={status === "submitting"}
        data-testid={isPartner ? "partner-form-submit-button" : "inquiry-form-submit-button"}
        className="btn-primary w-full sm:w-auto disabled:opacity-60"
      >
        {status === "submitting" ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
        {isPartner ? "Request Partner Information" : "Request a Conversation"}
      </button>
      <p className="text-xs leading-relaxed text-charcoal/55">
        Please don’t include patient records, sensitive medical details, or financial
        account information in this form.
      </p>
    </form>
  );
}
